"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (isOpen && !(event.target as HTMLElement).closest("#mobile-menu")) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  return (
    <header className="fixed top-0 left-0 w-full bg-[var(--background)] backdrop-blur-3xxl border-b border-gray-400 z-50">
      <div className="container mx-auto flex items-center justify-between px-6 py-4">
        {/* Brand Name */}
        <Link href="/">
          <span className="text-xl font-semibold text-white transition-transform duration-200 hover:scale-110 inline-block">Valdesio</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-6">
          <Link href="/#about-us" className="text-gray-400 hover:text-white transition">
            About Us
          </Link>
          <Link href="/#open-source" className="text-gray-400 hover:text-white transition">
            Open Source
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button className="md:hidden text-white focus:outline-none" onClick={() => setIsOpen(true)}>
          {/* SVG Hamburger Icon */}
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16m-7 6h7" />
          </svg>
        </button>
      </div>

      {/* Mobile Menu (Animated Popup) */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Blurred Overlay */}
            <motion.div
              className="fixed inset-0 bg-black/70 backdrop-blur-xl z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)} // Close when clicking outside
            />

            {/* Popup Overlay (Closes on Click) */}
            <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-xl z-50"
            onClick={() => setIsOpen(false)} // Close when clicking outside
            >
            {/* Popup Container */}
            <div 
                className="relative bg-white text-black rounded-2xl shadow-lg border border-white/50 w-3/4 max-w-sm p-6"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
            >
                {/* Close Button */}
                <button 
                className="absolute top-3 right-3 text-gray-700 hover:text-black transition-transform duration-200 hover:scale-110 inline-block"
                onClick={() => setIsOpen(false)}
                >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                </button>

                {/* Menu Items */}
                <nav className="flex flex-col items-center space-y-4">
                <Link href="/#about-us" className="text-gray-700 hover:text-black text-lg" onClick={() => setIsOpen(false)}>
                    About Us
                </Link>
                <Link href="/#open-source" className="text-gray-700 hover:text-black text-lg" onClick={() => setIsOpen(false)}>
                    Open Source
                </Link>
                </nav>
            </div>
            </motion.div>

          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
